import axios from "axios";
import { getAccessToken } from "./authSession";
import { API_BASE_URL } from "../config";

const getAuthHeaders = () => {
    const token = getAccessToken();
    if (!token) {
        window.location.href = "/";
        return {};
    }
    return {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    };
};

const baseUrl = `${API_BASE_URL}/integrations/marketplaces/rozetka`;

// --- Аккаунты ---

export const getRozetkaAccounts = async () => {
    const response = await axios.get(`${baseUrl}/accounts`, getAuthHeaders());
    return response.data;
};

export const createRozetkaAccount = async (payload) => {
    const response = await axios.post(`${baseUrl}/accounts`, payload, getAuthHeaders());
    return response.data;
};

export const updateRozetkaAccount = async (accountId, payload) => {
    const response = await axios.put(`${baseUrl}/accounts/${accountId}`, payload, getAuthHeaders());
    return response.data;
};

export const healthcheckRozetkaAccount = async (accountId) => {
    const response = await axios.post(
        `${baseUrl}/accounts/${accountId}/healthcheck`,
        {},
        { ...getAuthHeaders(), timeout: 30 * 1000 },
    );
    return response.data;
};

// --- Справочники ---

export const syncRozetkaCategories = async (accountId) => {
    const response = await axios.post(
        `${baseUrl}/accounts/${accountId}/reference/categories/sync`,
        {},
        { ...getAuthHeaders(), timeout: 60 * 1000 },
    );
    return response.data;
};

export const getRozetkaReferenceCategories = async (accountId, params = {}) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/reference/categories`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

export const suggestRozetkaCategories = async (accountId, params = {}) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/reference/categories/suggest`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

export const syncRozetkaAttributes = async (accountId, categoryId) => {
    const response = await axios.post(
        `${baseUrl}/accounts/${accountId}/reference/attributes/sync`,
        {},
        { ...getAuthHeaders(), params: { category_id: categoryId }, timeout: 60 * 1000 },
    );
    return response.data;
};

export const getRozetkaReferenceAttributes = async (accountId, categoryId) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/reference/attributes`, {
        ...getAuthHeaders(),
        params: { category_id: categoryId },
    });
    return response.data;
};

export const getRozetkaReferenceProducers = async (accountId, params = {}) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/reference/producers`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

// --- Маппинг категорий ---

export const getRozetkaCategoryMappings = async (accountId) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/category-mappings`, getAuthHeaders());
    return response.data;
};

export const upsertRozetkaCategoryMapping = async (accountId, payload) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/category-mappings`, payload, getAuthHeaders());
    return response.data;
};

export const getRozetkaInternalCategories = async (accountId, params = {}) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/internal-categories`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

export const getRozetkaInternalBrands = async (accountId, params = {}) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/internal-brands`, {
        ...getAuthHeaders(),
        params,
    });
    return response.data;
};

// --- Маппинг брендов/производителей ---

export const getRozetkaProducerMappings = async (accountId) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/producer-mappings`, getAuthHeaders());
    return response.data;
};

export const upsertRozetkaProducerMapping = async (accountId, payload) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/producer-mappings`, payload, getAuthHeaders());
    return response.data;
};

export const suggestRozetkaProducerMappings = async (accountId, internalBrandCode) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/producer-mappings/suggest`, {
        ...getAuthHeaders(),
        params: { internal_brand_code: internalBrandCode },
    });
    return response.data;
};

// --- Маппинг характеристик/значений ---

export const getRozetkaAttributeFeedFields = async (accountId, categoryId) => (
    await axios.get(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/feed-fields`, getAuthHeaders())
).data;

export const suggestRozetkaAttributeMappings = async (accountId, categoryId) => (
    await axios.post(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/suggest`, {}, getAuthHeaders())
).data;

export const approveSuggestedRozetkaAttributeMappings = async (accountId, categoryId, kind) => (
    await axios.post(
        `${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/bulk/approve-suggested`,
        {},
        { ...getAuthHeaders(), params: { kind } },
    )
).data;

export const getRozetkaAttributeMappings = async (accountId, categoryId) => (
    await axios.get(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}`, getAuthHeaders())
).data;

export const createRozetkaAttributeMapping = async (accountId, categoryId, data) => (
    await axios.post(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}`, data, getAuthHeaders())
).data;

export const updateRozetkaAttributeMapping = async (accountId, categoryId, mappingId, data) => (
    await axios.patch(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/${mappingId}`, data, getAuthHeaders())
).data;

export const getRozetkaAttributeValueMappings = async (accountId, categoryId, mappingId) => (
    await axios.get(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/${mappingId}/values`, getAuthHeaders())
).data;

export const createRozetkaAttributeValueMapping = async (accountId, categoryId, mappingId, data) => (
    await axios.post(
        `${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/${mappingId}/values`,
        data,
        getAuthHeaders(),
    )
).data;

export const updateRozetkaAttributeValueMapping = async (accountId, categoryId, mappingId, valueId, data) => (
    await axios.patch(
        `${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/${mappingId}/values/${valueId}`,
        data,
        getAuthHeaders(),
    )
).data;

export const getRozetkaAttributeMappingSummary = async (accountId, categoryId) => (
    await axios.get(`${baseUrl}/accounts/${accountId}/attribute-mapping/${categoryId}/summary/data`, getAuthHeaders())
).data;

// --- Отправители/цены ---

export const getRozetkaSupplierSettings = async (accountId) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/supplier-settings`, getAuthHeaders());
    return response.data;
};

export const updateRozetkaSupplierSettings = async (accountId, supplierCode, payload) => {
    const response = await axios.put(
        `${baseUrl}/accounts/${accountId}/supplier-settings/${encodeURIComponent(supplierCode)}`,
        payload,
        getAuthHeaders(),
    );
    return response.data;
};

// --- Фид ---

export const generateRozetkaFeed = async (accountId, payload = {}) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/feed/generate`, payload, {
        ...getAuthHeaders(),
        timeout: 10 * 60 * 1000,
    });
    return response.data;
};

// --- Точечный API каталога ---

export const createRozetkaProduct = async (accountId, productCode) => {
    const response = await axios.post(
        `${baseUrl}/accounts/${accountId}/products/create`,
        { product_code: productCode },
        getAuthHeaders(),
    );
    return response.data;
};

export const updateRozetkaProductContent = async (accountId, productCode) => {
    const response = await axios.put(
        `${baseUrl}/accounts/${accountId}/products/${encodeURIComponent(productCode)}/content`,
        {},
        getAuthHeaders(),
    );
    return response.data;
};

export const repairRozetkaMainImages = async (accountId, payload = { dry_run: true }) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/products/repair-main-images`, payload, {
        ...getAuthHeaders(),
        timeout: 10 * 60 * 1000,
    });
    return response.data;
};

export const syncRozetkaModerationStatus = async (accountId) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/products/moderation-status`, {}, getAuthHeaders());
    return response.data;
};

// --- Jobs ---

export const getRozetkaJobs = async (accountId) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/jobs`, getAuthHeaders());
    return response.data;
};

export const downloadRozetkaSkippedReport = async (accountId, jobId, filename = "rozetka_skipped_report.xlsx") => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/jobs/${jobId}/skipped-report`, {
        ...getAuthHeaders(),
        responseType: "blob",
    });
    const blobUrl = window.URL.createObjectURL(
        new Blob([response.data], {
            type: response.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
    );
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
};

// --- Остатки/цены ---

export const syncRozetkaProductLinks = async (accountId, payload = {}) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/products/sync-links`, payload, {
        ...getAuthHeaders(),
        timeout: 10 * 60 * 1000,
    });
    return response.data;
};

export const syncRozetkaStockPrices = async (accountId, payload = {}) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/stock-prices/sync`, payload, {
        ...getAuthHeaders(),
        timeout: 10 * 60 * 1000,
    });
    return response.data;
};

export const getRozetkaPriceQuarantine = async (accountId) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/price-quarantine`, getAuthHeaders());
    return response.data;
};

// --- Заказы (Этап 2, Фаза 3) ---

export const getRozetkaPaymentMappings = async (accountId) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/payment-mappings`, getAuthHeaders());
    return response.data;
};

export const createRozetkaPaymentMapping = async (accountId, payload) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/payment-mappings`, payload, getAuthHeaders());
    return response.data;
};

export const updateRozetkaPaymentMapping = async (accountId, mappingId, payload) => {
    const response = await axios.put(
        `${baseUrl}/accounts/${accountId}/payment-mappings/${mappingId}`,
        payload,
        getAuthHeaders(),
    );
    return response.data;
};

export const deleteRozetkaPaymentMapping = async (accountId, mappingId) => {
    const response = await axios.delete(`${baseUrl}/accounts/${accountId}/payment-mappings/${mappingId}`, getAuthHeaders());
    return response.data;
};

export const healthcheckRozetkaOrders = async (accountId) => {
    const response = await axios.post(
        `${baseUrl}/accounts/${accountId}/orders/healthcheck`,
        {},
        { ...getAuthHeaders(), timeout: 30 * 1000 },
    );
    return response.data;
};

export const dryRunRozetkaOrders = async (accountId, limit = 10) => {
    const response = await axios.post(
        `${baseUrl}/accounts/${accountId}/orders/dry-run`,
        {},
        { ...getAuthHeaders(), params: { limit }, timeout: 60 * 1000 },
    );
    return response.data;
};

export const getRozetkaOrders = async (accountId, limit = 50) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/orders`, {
        ...getAuthHeaders(),
        params: { limit },
    });
    return response.data;
};

// --- Статусы + ттн исходящие (Этап 2, Фаза 4) ---

export const getRozetkaOrderStatusMappings = async (accountId) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/order-status-mappings`, getAuthHeaders());
    return response.data;
};

export const createRozetkaOrderStatusMapping = async (accountId, payload) => {
    const response = await axios.post(`${baseUrl}/accounts/${accountId}/order-status-mappings`, payload, getAuthHeaders());
    return response.data;
};

export const updateRozetkaOrderStatusMapping = async (accountId, mappingId, payload) => {
    const response = await axios.put(
        `${baseUrl}/accounts/${accountId}/order-status-mappings/${mappingId}`,
        payload,
        getAuthHeaders(),
    );
    return response.data;
};

export const deleteRozetkaOrderStatusMapping = async (accountId, mappingId) => {
    const response = await axios.delete(
        `${baseUrl}/accounts/${accountId}/order-status-mappings/${mappingId}`,
        getAuthHeaders(),
    );
    return response.data;
};

export const getRozetkaOrderStatuses = async (accountId) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/order-statuses`, {
        ...getAuthHeaders(),
        timeout: 30 * 1000,
    });
    return response.data;
};

export const getRozetkaPaymentMethods = async (accountId) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/payment-methods`, {
        ...getAuthHeaders(),
        timeout: 30 * 1000,
    });
    return response.data;
};

export const getRozetkaSalesdriveStatuses = async (accountId) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/salesdrive-statuses`, getAuthHeaders());
    return response.data;
};

export const getRozetkaSalesdrivePaymentMethods = async (accountId) => {
    const response = await axios.get(`${baseUrl}/accounts/${accountId}/salesdrive-payment-methods`, getAuthHeaders());
    return response.data;
};

// --- Справочник способов оплаты SalesDrive (имя+код, ведётся вручную оператором) ---

export const getSalesdrivePaymentMethodsDirectory = async () => {
    const response = await axios.get(`${baseUrl}/salesdrive-payment-methods`, getAuthHeaders());
    return response.data;
};

export const createSalesdrivePaymentMethod = async (payload) => {
    const response = await axios.post(`${baseUrl}/salesdrive-payment-methods`, payload, getAuthHeaders());
    return response.data;
};

export const updateSalesdrivePaymentMethod = async (methodId, payload) => {
    const response = await axios.put(`${baseUrl}/salesdrive-payment-methods/${methodId}`, payload, getAuthHeaders());
    return response.data;
};

export const deleteSalesdrivePaymentMethod = async (methodId) => {
    const response = await axios.delete(`${baseUrl}/salesdrive-payment-methods/${methodId}`, getAuthHeaders());
    return response.data;
};

// --- Чеки Checkbox → Rozetka ---
export const getRozetkaPrro = async (accountId) => (
    await axios.get(`${baseUrl}/accounts/${accountId}/receipts/prro`, getAuthHeaders())
).data;

export const connectRozetkaManualPrro = async (accountId) => (
    await axios.post(`${baseUrl}/accounts/${accountId}/receipts/manual-connect`, {}, getAuthHeaders())
).data;

export const getRozetkaReceipts = async (accountId, params = {}) => (
    await axios.get(`${baseUrl}/accounts/${accountId}/receipts`, { ...getAuthHeaders(), params })
).data;

export const retryRozetkaReceipt = async (accountId, taskId) => (
    await axios.post(`${baseUrl}/accounts/${accountId}/receipts/${taskId}/retry`, {}, getAuthHeaders())
).data;
